"use strict";

const gating = require("./walletGating");

/**
 * DGF-3054 — mounts express wallet buttons into a container.
 *
 * Replaces the vendor's render orchestration (app_adyen_SFRA/.../adyen/express/cart and /product),
 * which cannot be edited, and fixes two defects in it:
 *
 *   1. Unguarded mounts. All four vendor mounts are unconditional (cart/expressPayments.js:63, 88,
 *      111, 129), yet ApplePay.getComponent() returns null when the device cannot use Apple Pay.
 *      On any non-Apple device line 111 throws "Cannot read properties of null (reading 'mount')".
 *      Because each renderer is a separate async handler the loop survives, so the visible result
 *      is a blank button placeholder on a payment surface plus a console error.
 *   2. Placeholders left behind. The vendor inserts every button element up-front with
 *      replaceChildren, so an unavailable wallet leaves an empty element in the DOM. Here the
 *      element is only kept once a component has actually mounted.
 *
 * All DOM access goes through the injected `doc` so this module is testable under jest's node
 * environment.
 */

const MOUNTED_ATTRIBUTE = "data-express-mounted";

// DGF-4659 — business cap on how many express wallets may mount on one surface (so the shopper
// sees at most this many wallets plus the checkout CTA). Read from the container so the server's
// ExpressPayments_MaxWallets preference (adyenConfigs.getExpressMaxWallets) reaches the client the
// same way the allow-list and local-wallet attributes do. 2 is the business default here too: a
// missing, non-numeric or non-positive attribute must still cap, never mean "unlimited".
const MAX_WALLETS_ATTRIBUTE = "data-express-max-wallets";
const DEFAULT_MAX_WALLETS = 2;

/**
 * Reads the per-container wallet cap, defensively.
 *
 * @param {Object} container - element with a getAttribute method
 * @returns {number} the cap to apply; DEFAULT_MAX_WALLETS when the attribute is missing or invalid
 */
function readMaxWallets(container) {
    if (!container || typeof container.getAttribute !== "function") {
        return DEFAULT_MAX_WALLETS;
    }
    const parsed = parseInt(container.getAttribute(MAX_WALLETS_ATTRIBUTE), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_MAX_WALLETS;
}

// DGF-4617 — the await on component.mount() below (buttons.render()) has no ceiling of its own:
// a blocked iframe, an adblocker or a dead network can leave that promise pending forever. Without
// a timeout renderWallets() never returns, which wedges every caller that awaits it — cartWallets.js
// leaves data-express-rendering="true" on the container so every later cart:update is dropped as
// already-in-progress, and productWallets.js's mount lock never advances so no further variant
// selection ever renders. 10s is generous for a same-origin iframe render; anything past that is a
// stalled mount, not a slow one.
const MOUNT_TIMEOUT_MS = 10000;

/**
 * Removes a wallet's placeholder, tolerating a container another pass has already emptied.
 *
 * REVIEW FINDING: both removal sites ran `container.removeChild(element)` unguarded. A concurrent
 * refresh that clears the container out from under an in-flight mount — precisely the scenario the
 * mount timeout exists to survive — leaves the element already detached, and removeChild then
 * throws NotFoundError straight out of renderWallets, past the catch that was meant to contain a
 * failed mount. The caller's locks (`data-express-rendering` on the bag, the PDP's mount lock) make
 * that hard to reach today, but the whole point of these two call sites is to clean up after a
 * failure, and cleanup that can itself throw is not cleanup.
 *
 * @param {Object} container - the express container
 * @param {Object} element - the placeholder to remove
 * @returns {void}
 */
function detachElement(container, element) {
    if (!container || !element || typeof container.removeChild !== "function") {
        return;
    }

    if (element.parentNode && element.parentNode !== container) {
        return;
    }

    if (!element.parentNode) {
        return;
    }

    container.removeChild(element);
}

/**
 * Races a component mount against a fixed timeout, so a mount promise that never settles cannot
 * wedge the caller forever.
 *
 * REJECTS on both failure paths — a thrown mount and a timeout — which is what the single catch
 * around the call site expects. (An earlier version of this docstring claimed it resolved a
 * `{timedOut}` shape and never rejected; it never did, and reasoning from that would suggest the
 * caller's catch was dead code.)
 *
 * @param {Promise} mountPromise - the promise returned by component.mount(element)
 * @returns {Promise<*>} resolves with the mount's own value; rejects if it throws or times out
 */
function mountWithTimeout(mountPromise) {
    return new Promise(function race(resolve, reject) {
        let settled = false;
        const timer = setTimeout(function onTimeout() {
            if (settled) {
                return;
            }
            settled = true;
            reject(new Error("mount timed out"));
        }, MOUNT_TIMEOUT_MS);

        Promise.resolve(mountPromise).then(
            function onMount(value) {
                if (settled) {
                    return;
                }
                settled = true;
                clearTimeout(timer);
                resolve(value);
            },
            function onMountError(error) {
                if (settled) {
                    return;
                }
                settled = true;
                clearTimeout(timer);
                reject(error);
            }
        );
    });
}

/**
 * Builds the element a single wallet component mounts into.
 *
 * @param {Object} doc - document-like object
 * @param {string} walletType - "applepay" or "googlepay"
 * @param {string} surface - "cart", "minicart" or "pdp"
 * @returns {Object} the created element
 */
function createWalletElement(doc, walletType, surface) {
    const element = doc.createElement("div");
    element.setAttribute("id", `${walletType}-${surface}`);
    element.setAttribute("class", `expressComponent express-btn express-btn--${walletType}`);
    element.setAttribute("data-method", walletType);
    return element;
}

/**
 * Renders the wallets for one container.
 *
 * Every wallet is resolved independently: an unavailable or unsupported wallet cannot stop the
 * other from rendering, and only wallets that mounted contribute an element to the DOM.
 *
 * DGF-4659 — capped at `container`'s data-express-max-wallets (business default 2), counting only
 * wallets that actually mounted. A wallet that failed its availability check or its mount does not
 * consume a slot, so e.g. Apple Pay being unavailable lets PayPal and Google Pay both mount under a
 * cap of two. The cap is applied in priority order (`walletTypes`, already sorted by
 * ExpressPayments_order in cartWallets.resolveWalletTypes/sortByExpressOrder — that sort now covers
 * local wallets too, not just what Adyen returned) without re-sorting here, so once the cap is
 * reached every remaining wallet is recorded in `skipped` with reason "max-wallets-reached" and
 * never has its component built.
 *
 * @param {Object} options - render options
 * @param {Object} options.doc - document-like object
 * @param {Object} options.container - element to render into
 * @param {string} options.surface - "cart", "minicart" or "pdp"
 * @param {string[]} options.walletTypes - wallet names from the payment methods response
 * @param {Object} options.adyenPaymentMethods - AdyenPaymentMethods node of the response
 * @param {Function} options.createComponent - async (walletType, walletConfiguration, element) =>
 *        component|null; owns SDK construction so this module stays vendor-agnostic
 * @param {Object} [options.nav] - navigator-like object, injected for testing
 * @returns {Promise<Object>} { mounted: string[], skipped: Array<{wallet, reason}> }
 */
async function renderWallets(options) {
    const doc = options.doc;
    const container = options.container;
    const surface = options.surface;
    const supported = gating.filterSupportedWallets(options.walletTypes);
    const result = { mounted: [], skipped: [] };
    const maxWallets = readMaxWallets(container);

    if (!container) {
        return result;
    }

    // Clear anything from a previous render before re-mounting, so a remount cannot double up.
    while (container.firstChild) {
        container.removeChild(container.firstChild);
    }
    container.removeAttribute(MOUNTED_ATTRIBUTE);

    const unsupported = (options.walletTypes || []).filter((type) => supported.indexOf(type) === -1);
    unsupported.forEach((type) => {
        result.skipped.push({ wallet: type, reason: "no-renderer" });
    });

    for (let i = 0; i < supported.length; i += 1) {
        const walletType = supported[i];

        // DGF-4659 — checked first and before any component is built: only a SUCCESSFUL mount
        // consumes a slot (result.mounted grows only below, once mount actually resolves), so a
        // wallet skipped for availability or a failed mount earlier in this loop never counted
        // against the cap and does not cost the next wallet its slot.
        if (result.mounted.length >= maxWallets) {
            result.skipped.push({ wallet: walletType, reason: "max-wallets-reached" });
            continue;
        }

        if (gating.isWalletExcludedOnPlatform(walletType, options.nav)) {
            result.skipped.push({ wallet: walletType, reason: "platform-excluded" });
            continue;
        }

        // The territory gate is Adyen's /paymentMethods response, so it only applies to Adyen
        // wallets. A local wallet (DGF-4617 direct PayPal) reaches here only when the server
        // listed it on the container, and its own SDK decides availability below.
        const walletConfiguration = gating.isLocalWalletOnContainer(container, walletType)
            ? {}
            : gating.getWalletConfiguration(options.adyenPaymentMethods, walletType);
        if (!walletConfiguration) {
            // Adyen did not offer this wallet for the shopper's country, currency and amount.
            result.skipped.push({ wallet: walletType, reason: "not-available-in-region" });
            continue;
        }

        const element = createWalletElement(doc, walletType, surface);
        container.appendChild(element);

        let component = null;
        try {
            // eslint-disable-next-line no-await-in-loop
            component = await options.createComponent(walletType, walletConfiguration, element);
        } catch (error) {
            component = null;
        }

        // eslint-disable-next-line no-await-in-loop
        const available = component ? await gating.isComponentAvailable(component) : false;

        if (!component || !available) {
            // No blank placeholder is left behind on a payment surface.
            detachElement(container, element);
            result.skipped.push({ wallet: walletType, reason: "not-available-on-device" });
            continue;
        }

        try {
            // Await is load-bearing here even though Adyen's own components mount
            // synchronously (a resolved-immediately promise is also fine to await). DGF-4617's
            // direct PayPal component is the first whose mount() returns buttons.render(element),
            // a real promise: without awaiting it, a rejected render still falls through to the
            // "mounted" branch below — the wallet is counted as mounted, the container is marked
            // data-express-mounted, and the surface is revealed with a blank slot where the button
            // should be. This is exactly what a second rapid basket refresh triggers: it clears the
            // container out from under the first pass's still-in-flight render.
            // eslint-disable-next-line no-await-in-loop
            await mountWithTimeout(component.mount(element));
            result.mounted.push(walletType);
        } catch (error) {
            detachElement(container, element);
            result.skipped.push({ wallet: walletType, reason: "mount-failed" });
        }
    }

    if (result.mounted.length) {
        container.setAttribute(MOUNTED_ATTRIBUTE, "true");
    }

    return result;
}

module.exports = {
    MOUNTED_ATTRIBUTE: MOUNTED_ATTRIBUTE,
    MOUNT_TIMEOUT_MS: MOUNT_TIMEOUT_MS,
    MAX_WALLETS_ATTRIBUTE: MAX_WALLETS_ATTRIBUTE,
    DEFAULT_MAX_WALLETS: DEFAULT_MAX_WALLETS,
    createWalletElement: createWalletElement,
    renderWallets: renderWallets
};
